/**
 * US-013 — desktop (Electron) capability detection + helpers.
 *
 * All web-safe: on the web `window.livingsyncs` is undefined, `isElectron` is
 * false, and every helper degrades to the existing web behavior. This is the
 * single contract for the preload bridge exposed by the desktop shell — keep it
 * in sync with `desktop/src/preload/index.ts`.
 */

export interface LivingSyncsTokenStore {
  /** False when OS encryption (safeStorage) is unavailable — keep the default store. */
  available: boolean;
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

/** Classified auto-update failure. Mirrors desktop/src/main/updater.ts. */
export type UpdateErrorKind = "signature" | "download" | "unknown";

/**
 * US-176 Phase 2 — the Electron half of the device-bound re-auth credential.
 * Same method shape as the iOS Capacitor plugin it mirrors
 * (packages/ui/src/lib/native/reauthCredential.ts's ReauthCredentialPlugin),
 * safeStorage-backed instead of Secure-Enclave-backed
 * (desktop/src/main/reauthCredential.ts).
 */
export interface LivingSyncsReauthCredential {
  /** False when OS encryption (safeStorage) is unavailable — mirrors
   * LivingSyncsTokenStore's own `available` field/reasoning. */
  available: boolean;
  mint(): Promise<{ publicKeyPem: string }>;
  sign(nonce: string): Promise<{ signature: string }>;
  exists(): Promise<{ exists: boolean }>;
  clear(): Promise<void>;
}

export interface LivingSyncsBridge {
  backendUrl: string;
  getPlatform(): string;
  getAppVersion(): string;
  tokenStore: LivingSyncsTokenStore;
  reauthCredential: LivingSyncsReauthCredential;
  saveExport(
    filename: string,
    data: ArrayBuffer,
  ): Promise<{ saved: boolean; filePath?: string }>;
  purge(): Promise<void>;
  onLock(cb: () => void): () => void;
  onSignOut(cb: () => void): () => void;
  onDeepLink(cb: (route: string) => void): () => void;
  onUpdateDownloaded(cb: (version: string) => void): () => void;
  /**
   * Auto-update failed AFTER downloading. `kind` is classified in the main
   * process; the raw error never crosses. "signature" means the installed app
   * and the update are signed differently — restarting can never fix it, only
   * a fresh install can, so the UI must not tell the user to try again.
   */
  onUpdateError(cb: (kind: UpdateErrorKind) => void): () => void;
  quitAndInstall(): void;
  /**
   * Native OS notification (US-018 AC1/AC2). `body` is shown as OS-level
   * UI, potentially visible on a lock screen or to someone glancing at
   * another person's screen -- callers MUST pass generic, non-PHI text
   * (e.g. "New incident reported", never a resident's name or other
   * identifying detail). `route` is an in-app path to navigate to on
   * click (e.g. "/incidents/123"); omit for a generic "open the app"
   * notification.
   */
  showNotification(payload: {
    title: string;
    body: string;
    route?: string;
  }): void;
  /** Dock badge count (US-018 AC4). 0 clears the badge. */
  setBadgeCount(count: number): void;
}

/** Capacitor's own global, injected into the webview before app JS runs. */
interface CapacitorGlobal {
  isNativePlatform(): boolean;
}

declare global {
  interface Window {
    livingsyncs?: LivingSyncsBridge;
    Capacitor?: CapacitorGlobal;
  }
}

/**
 * The native shell bridge, or undefined on the web.
 *
 * Named `desktop` for historical reasons (US-013); as of US-015 it is also
 * installed by the Capacitor iPad shell, which implements the same contract.
 * Prefer the capability flags below over checking this directly.
 *
 * `let`, not `const` — fixed 2026-09-12 (second occurrence of the same bug
 * class, see gotcha_barrel_import_freezes_platform_bridge_undefined). A plain
 * `const desktop = window.livingsyncs` computed once at THIS module's own
 * load time is only correct if this module is guaranteed to evaluate AFTER
 * capacitorBridge.ts's `installCapacitorBridge()` call — a guarantee that
 * held for the literal source-import order in main.tsx, but broke silently
 * once Rollup's automatic chunking (this file is shared with useAuth.ts,
 * which is also reachable from lazy-loaded route chunks) split this module
 * into a SEPARATE output chunk from capacitorBridge.ts. Confirmed live via a
 * diagnostic fetch from a real device build: `desktop` was frozen undefined
 * even though `window.livingsyncs` was already correctly populated
 * (`sameRef: false`) — changing the IMPORT SPECIFIER that reaches this file
 * (a deep import instead of the barrel) did NOT fix it, because Rollup's
 * chunk-splitting decision is keyed on the resolved file, not the specifier
 * string, so the same file still landed in the same shared chunk regardless.
 * `refreshDesktopBridge()` below lets capacitorBridge.ts correct this value
 * EXPLICITLY, right after it actually sets `window.livingsyncs` — making
 * correctness independent of which chunk happens to evaluate first. Every
 * real consumer in this codebase reads `desktop` lazily (inside a React
 * effect or a function body, never synchronously at its own module's
 * top level), so a later correction here is always visible in time, via
 * ordinary ES module live-binding semantics (`import { desktop }` is a live
 * view into this module's own variable, not a one-time copy).
 */
export let desktop: LivingSyncsBridge | undefined =
  typeof window !== "undefined" ? window.livingsyncs : undefined;

/**
 * True inside the Capacitor (iPad) shell — US-015 AC18.
 *
 * Keyed off Capacitor's OWN global, not the bridge's presence and not
 * `getPlatform()`'s string. Both shells install the same `window.livingsyncs`,
 * so bridge presence cannot tell them apart; and `getPlatform()` returns
 * `process.platform` under Electron, so a string compare would misfire.
 *
 * Unaffected by the `desktop` race above: `window.Capacitor` is injected by
 * the native layer before any page JS runs at all, so it is never a snapshot
 * of something our own JS is still in the middle of setting up.
 */
export const isCapacitor =
  typeof window !== "undefined" &&
  window.Capacitor?.isNativePlatform() === true;

/**
 * True ONLY inside the Electron desktop shell.
 *
 * The `!isCapacitor` term is load-bearing: US-015's Capacitor bridge installs
 * the same `window.livingsyncs`, so a bare `desktop !== undefined` reported
 * true on iPad — the misnomer AC18 exists to fix.
 *
 * `let`, kept in sync with `desktop` by `refreshDesktopBridge()` — see that
 * function's own comment.
 */
export let isElectron = desktop !== undefined && !isCapacitor;

/**
 * True inside any native shell (Electron or Capacitor) — US-015 AC18.
 *
 * `let`, kept in sync with `desktop` by `refreshDesktopBridge()`.
 */
export let isNative = isElectron || isCapacitor;

/**
 * Re-derive `desktop`/`isElectron`/`isNative` from the CURRENT
 * `window.livingsyncs`, instead of trusting whatever this module's own
 * top-level code captured at whatever moment it happened to first evaluate.
 *
 * `capacitorBridge.ts` calls this immediately after actually setting
 * `window.livingsyncs`, so every consumer sees the correct value regardless
 * of chunk evaluation order (see `desktop`'s own doc comment for the full
 * mechanism this closes). The Electron preload does not need to call this:
 * it runs in an isolated context that sets `window.livingsyncs` before the
 * renderer's own JS starts at all, so Electron never had this race.
 */
export function refreshDesktopBridge(): void {
  desktop = typeof window !== "undefined" ? window.livingsyncs : undefined;
  isElectron = desktop !== undefined && !isCapacitor;
  isNative = isElectron || isCapacitor;
}

/**
 * Public web origin for links embedded in EMAILS (invitations) — must be the
 * hosted web app, never the desktop app origin (`livingsyncs://app`). Set
 * `VITE_PUBLIC_APP_URL` for desktop builds; on the web it defaults to the
 * current origin, so behavior is unchanged.
 */
export function getPublicAppUrl(): string {
  const configured = import.meta.env.VITE_PUBLIC_APP_URL as string | undefined;
  if (configured && configured.length > 0) return configured.replace(/\/$/, "");
  return typeof window !== "undefined" ? window.location.origin : "";
}
